// ✅ src/pages/VehicleV2/ServiceExpiryAlert.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/serviceExpiryAlert", serviceExpiryAlert);
//
// APIs used:
//   GET  https://nissi-india.com/api/serviceExpiryAlert/config?loginUserId=..
//   GET  https://nissi-india.com/api/serviceExpiryAlert/data?loginUserId=..
//   POST https://nissi-india.com/api/serviceExpiryAlert/save
//
// ✅ Updated to match VehicleFuelRate page pattern:
// - Uses common localStorage header pattern
// - Reads loginUserId from session/local storage
// - Loads rights first, then page data
// - Save disabled if no add rights / branch inactive
// - Same UI style pattern as entry pages
// - Back button goes to previous page

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaSave, FaCheckSquare, FaSquare } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/serviceExpiryAlert";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const defaultAlerts = {
  insurance: { enabled: false, days: 0, software: false, sms: false, email: false },
  fc: { enabled: false, days: 0, software: false, sms: false, email: false },
  roadTax: { enabled: false, days: 0, software: false, sms: false, email: false },
  permit: { enabled: false, days: 0, software: false, sms: false, email: false },
  periodicHr: { enabled: false, value: 0, software: false, sms: false, email: false },
  periodicKm: { enabled: false, value: 0, software: false, sms: false, email: false },
};

const ownVehicleRows = [
  { key: "insurance", label: "Insurance" },
  { key: "fc", label: "FC" },
  { key: "roadTax", label: "RoadTax" },
  { key: "permit", label: "Permit" },
];

const periodicRows = [
  { key: "periodicHr", label: "Periodic-HR" },
  { key: "periodicKm", label: "Periodic-KM" },
];

const ServiceExpiryAlert = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ Shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // rights / branch active
  const branchActive = useMemo(() => {
    const v = sessionStorage.getItem("BranchActive") || localStorage.getItem("BranchActive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const [allowed, setAllowed] = useState(true);
  const [canSaveRight, setCanSaveRight] = useState(true);

  const [alerts, setAlerts] = useState(defaultAlerts);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const canSave = useMemo(() => {
    return !!branchActive && !!canSaveRight;
  }, [branchActive, canSaveRight]);

  const selectAllUsers = useMemo(() => {
    return users.length > 0 && users.every((u) => !!u.selected);
  }, [users]);

  const inputClass = `w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const sectionCardClass = `rounded-xl p-6 border ${
    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
  }`;

  const tableWrapClass = `rounded-lg border overflow-hidden ${
    isDark ? "border-gray-700" : "border-gray-200"
  }`;

  const Checkbox = ({ checked, onChange, disabled = false }) => (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`flex items-center justify-center w-5 h-5 ${
        disabled ? "opacity-60 cursor-not-allowed" : ""
      }`}
      title={disabled ? "No rights" : ""}
    >
      {checked ? (
        <FaCheckSquare className="w-5 h-5 text-blue-600" />
      ) : (
        <FaSquare className="w-5 h-5 text-gray-400" />
      )}
    </button>
  );

  const ToggleButton = ({ enabled, onChange, disabled = false }) => (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      disabled={disabled}
      className={`w-12 h-6 rounded-full transition-colors duration-200 flex items-center ${
        enabled ? `${buttonClass} justify-end` : "bg-gray-300 dark:bg-gray-600 justify-start"
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      title={disabled ? "No rights" : ""}
    >
      <div className="w-4 h-4 rounded-full bg-white mx-1 transition-transform duration-200" />
    </button>
  );

  const handleAlertChange = useCallback((alertKey, field, value) => {
    setAlerts((prev) => ({
      ...prev,
      [alertKey]: {
        ...prev[alertKey],
        [field]: value,
      },
    }));
  }, []);

  const handleUserSelect = useCallback((id) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === id ? { ...u, selected: !u.selected } : u))
    );
  }, []);

  const handleSelectAllUsers = useCallback(() => {
    const next = !selectAllUsers;
    setUsers((prev) => prev.map((u) => ({ ...u, selected: next })));
  }, [selectAllUsers]);

  /* ------------------------- LOAD CONFIG ------------------------- */
  const loadConfig = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/config`, {
      params: { loginUserId },
      headers: commonHeaders,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to load config");
    }

    setAllowed(data?.allowed !== false);
    setCanSaveRight(!!data?.canSave);

    return data;
  }, [loginUserId, commonHeaders]);

  /* ------------------------- LOAD DATA ------------------------- */
  const loadPageData = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/data`, {
      params: { loginUserId },
      headers: commonHeaders,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to load service expiry data");
    }

    setAlerts(data?.alerts ? { ...defaultAlerts, ...data.alerts } : defaultAlerts);
    setUsers(Array.isArray(data?.users) ? data.users : []);

    if (data?.canSave !== undefined) {
      setCanSaveRight(!!data.canSave);
    }
  }, [loginUserId, commonHeaders]);

  /* ------------------------- INITIAL LOAD ------------------------- */
  const loadAll = useCallback(async () => {
    try {
      setPageLoading(true);

      const cfg = await loadConfig();

      if (cfg?.allowed === false) {
        return;
      }

      await loadPageData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load service expiry alert");
    } finally {
      setPageLoading(false);
    }
  }, [loadConfig, loadPageData]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleClear = () => {
    setAlerts(defaultAlerts);
    setUsers((prev) => prev.map((u) => ({ ...u, selected: false })));
  };

  const handleResetFromServer = async () => {
    await loadAll();
  };

  const handleSave = async () => {
    if (!branchActive) {
      toast.error("Branch is not active. Save not allowed.");
      return;
    }

    if (!canSaveRight) {
      toast.error("You do not have rights to save service expiry alert.");
      return;
    }

    const selectedUserIds = users.filter((u) => !!u.selected).map((u) => u.id);

    const payload = {
      loginUserId: toInt(loginUserId, 0),
      alerts,
      selectedUserIds,
    };

    try {
      setLoading(true);

      const { data } = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to save service expiry alert");
      }

      toast.success(data?.message || "Saved Successfully");
      await loadAll();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to save service expiry alert"
      );
    } finally {
      setLoading(false);
    }
  };

  const renderOwnVehicleRows = () =>
    ownVehicleRows.map((service) => (
      <tr key={service.key} className={isDark ? "bg-gray-900" : "bg-white"}>
        <td className="px-4 py-3 font-medium">{service.label}</td>

        <td className="px-4 py-3 text-center">
          <div className="flex justify-center">
            <ToggleButton
              enabled={!!alerts[service.key]?.enabled}
              onChange={(value) => handleAlertChange(service.key, "enabled", value)}
              disabled={pageLoading || loading || !canSave}
            />
          </div>
        </td>

        <td className="px-4 py-3 text-center">
          <input
            type="number"
            value={alerts[service.key]?.days ?? 0}
            onChange={(e) =>
              handleAlertChange(service.key, "days", toInt(e.target.value, 0))
            }
            className={`w-24 text-center mx-auto ${inputClass}`}
            disabled={!alerts[service.key]?.enabled || pageLoading || loading || !canSave}
          />
        </td>

        <td className="px-4 py-3 text-center">
          <div className="flex justify-center">
            <Checkbox
              checked={!!alerts[service.key]?.software}
              onChange={() =>
                handleAlertChange(service.key, "software", !alerts[service.key]?.software)
              }
              disabled={pageLoading || loading || !canSave}
            />
          </div>
        </td>

        <td className="px-4 py-3 text-center">
          <div className="flex justify-center">
            <Checkbox
              checked={!!alerts[service.key]?.sms}
              onChange={() =>
                handleAlertChange(service.key, "sms", !alerts[service.key]?.sms)
              }
              disabled={pageLoading || loading || !canSave}
            />
          </div>
        </td>

        <td className="px-4 py-3 text-center">
          <div className="flex justify-center">
            <Checkbox
              checked={!!alerts[service.key]?.email}
              onChange={() =>
                handleAlertChange(service.key, "email", !alerts[service.key]?.email)
              }
              disabled={pageLoading || loading || !canSave}
            />
          </div>
        </td>
      </tr>
    ));

  const renderPeriodicRows = () =>
    periodicRows.map((service) => (
      <tr key={service.key} className={isDark ? "bg-gray-900" : "bg-white"}>
        <td className="px-4 py-3 font-medium">{service.label}</td>

        <td className="px-4 py-3 text-center">
          <div className="flex justify-center">
            <ToggleButton
              enabled={!!alerts[service.key]?.enabled}
              onChange={(value) => handleAlertChange(service.key, "enabled", value)}
              disabled={pageLoading || loading || !canSave}
            />
          </div>
        </td>

        <td className="px-4 py-3 text-center">
          <input
            type="number"
            value={alerts[service.key]?.value ?? 0}
            onChange={(e) =>
              handleAlertChange(service.key, "value", toInt(e.target.value, 0))
            }
            className={`w-28 text-center mx-auto ${inputClass}`}
            disabled={!alerts[service.key]?.enabled || pageLoading || loading || !canSave}
          />
        </td>

        <td className="px-4 py-3 text-center">
          <div className="flex justify-center">
            <Checkbox
              checked={!!alerts[service.key]?.software}
              onChange={() =>
                handleAlertChange(service.key, "software", !alerts[service.key]?.software)
              }
              disabled={pageLoading || loading || !canSave}
            />
          </div>
        </td>

        <td className="px-4 py-3 text-center">
          <div className="flex justify-center">
            <Checkbox
              checked={!!alerts[service.key]?.sms}
              onChange={() =>
                handleAlertChange(service.key, "sms", !alerts[service.key]?.sms)
              }
              disabled={pageLoading || loading || !canSave}
            />
          </div>
        </td>

        <td className="px-4 py-3 text-center">
          <div className="flex justify-center">
            <Checkbox
              checked={!!alerts[service.key]?.email}
              onChange={() =>
                handleAlertChange(service.key, "email", !alerts[service.key]?.email)
              }
              disabled={pageLoading || loading || !canSave}
            />
          </div>
        </td>
      </tr>
    ));

  if (!allowed) {
    return (
      <VehicleLayoutWrapper>
        <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
          <HeaderBar
            title={<span className="font-dmserif text-3xl">SERVICE EXPIRY ALERT</span>}
            setSkin={setSkin}
            currentSkin={skin}
          />

          <div
            className={`p-6 mt-6 rounded-2xl shadow-xl border ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
                : `bg-white ${currentSkin.glass} text-black`
            }`}
          >
            <div className="text-center text-lg font-semibold">
              You dont have rights to access this page
            </div>

            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </VehicleLayoutWrapper>
    );
  }

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SERVICE EXPIRY ALERT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Service Expiry Alert Settings
              </h1>

              {pageLoading && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>

          <div className="space-y-8">
            {/* Own Vehicle Expiry Alert */}
            <div className={sectionCardClass}>
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
                OWN VEHICLE EXPIRY ALERT
              </h2>

              <div className={tableWrapClass}>
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Service</th>
                      <th className="px-4 py-3 text-center font-semibold">Enable</th>
                      <th className="px-4 py-3 text-center font-semibold">Days</th>
                      <th className="px-4 py-3 text-center font-semibold">Software</th>
                      <th className="px-4 py-3 text-center font-semibold">SMS</th>
                      <th className="px-4 py-3 text-center font-semibold">EMAIL</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {renderOwnVehicleRows()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Periodic Expiry Alert */}
            <div className={sectionCardClass}>
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
                PERIODIC EXPIRY ALERT
              </h2>

              <div className={tableWrapClass}>
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Service</th>
                      <th className="px-4 py-3 text-center font-semibold">Enable</th>
                      <th className="px-4 py-3 text-center font-semibold">Value</th>
                      <th className="px-4 py-3 text-center font-semibold">Software</th>
                      <th className="px-4 py-3 text-center font-semibold">SMS</th>
                      <th className="px-4 py-3 text-center font-semibold">EMAIL</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {renderPeriodicRows()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Alert Recipients */}
            <div className={sectionCardClass}>
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
                <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Alert Recipients
                </h2>

                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Select All
                  </span>
                  <Checkbox
                    checked={selectAllUsers}
                    onChange={handleSelectAllUsers}
                    disabled={pageLoading || loading || !canSave}
                  />
                </div>
              </div>

              <div
                className={`rounded-lg border overflow-hidden max-h-96 overflow-y-auto ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div className={`${buttonClass} text-white grid grid-cols-1 md:grid-cols-4 gap-4 px-4 py-3 font-semibold`}>
                  <div>User Name</div>
                  <div>Mobile No</div>
                  <div>Email</div>
                  <div className="text-left md:text-center">Select</div>
                </div>

                {users.map((user, index) => (
                  <div
                    key={user.id}
                    className={`grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border-b ${
                      isDark ? "border-gray-700 hover:bg-gray-800" : "border-gray-200 hover:bg-gray-50"
                    } ${
                      index % 2 === 0
                        ? isDark
                          ? "bg-gray-900"
                          : "bg-gray-50"
                        : isDark
                        ? "bg-gray-800"
                        : "bg-white"
                    }`}
                  >
                    <div className="flex items-center">{toStr(user.userName)}</div>
                    <div className="flex items-center">{toStr(user.mobileNo)}</div>
                    <div className="flex items-center text-sm break-all">{toStr(user.email)}</div>
                    <div className="flex items-center md:justify-center">
                      <Checkbox
                        checked={!!user.selected}
                        onChange={() => handleUserSelect(user.id)}
                        disabled={pageLoading || loading || !canSave}
                      />
                    </div>
                  </div>
                ))}

                {users.length === 0 && (
                  <div className={`p-4 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    No users found
                  </div>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleClear}
                disabled={loading || pageLoading}
                className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Clear
              </button>

              <button
                type="button"
                onClick={handleResetFromServer}
                disabled={loading || pageLoading}
                className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Reset
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={loading || pageLoading || !canSave}
                className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  loading || pageLoading || !canSave
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
                title={!canSave ? "No rights to save" : ""}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    Save Alert Settings
                  </>
                )}
              </button>
            </div>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : SERVICE EXPIRY ALERT
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default ServiceExpiryAlert;